
import React, {useCallback, useEffect, useState} from 'react';
import {Alert, AlertTitle} from '@material-ui/lab';
import style from './style.module.scss';
import {IProps, IHandlers} from './types';
import {Button, Checkbox, createStyles, FormControlLabel, Theme, Typography, withStyles} from '@material-ui/core';
import SimpleDialog from "app/common/SimpleDialog";
import {renderIf} from "app/services/reactUtils.service";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import DoneIcon from '@material-ui/icons/Done';
import {makeStyles} from "@material-ui/core/styles";
import classNames from 'classnames';
import { BookingStatusNames, StatusCancelled, bookingStatusType } from 'shared-types/index';

import MuiButton from '@material-ui/core/Button';
import AppThemeService from 'app/services/theme/appTheme.service';

const NS = 'EditBookingFooter';


/**
 * Material UI styles have high specificity than the style.module.scss classes, so modifications should be done here
 */
function getMaterialStyles() {
  return makeStyles((theme: Theme) => {

    return createStyles({
      checkboxFC: {
        margin: '0 10px 0 0'
      },
      checkbox: {
        color: theme.palette.primary.main,
        padding: '6px 9px'
      },
      btnDefault: {
        color: theme.palette.primary.main
      },
      btnLeft: {
        padding: '6px 14px',
        marginRight: 10
      },
      btnRight: {
        padding: '6px 14px',
        marginLeft: 10
      }
    });
  });
}

const {palette} = AppThemeService.getAppTheme();
export default function EditBookingFooter({
    isManagerAndLocalEnv, showDateChange, bookingStatusName, bookingStatusCode, errorMsg, isLoading, isStandbyOrWaitlist, isSaveEnabled, isSmsEnabled, disableNotification,
    handleDeleteConfirmed, handleDeleteCancelled, handleSaveClick, handleDateChangeClick, paxThresholdError, tabValidationError,
    handleCloseClick, handleSendSMSClick, handleResendConfirmationUpdate, handleResendSMSConfirmationUpdate, customer, isStatusNameInList,
    isLockedBooking
}: IProps & IHandlers) {
  const SaveBtn = withStyles({
    root: {
      color: !tabValidationError ? '#fff' : 'rgba(0, 0, 0, 0.26)',
      backgroundColor: !tabValidationError ? palette.primary.main : '#616161'
    }
  })(MuiButton);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const [resendConfirmation, setResendConfirmation] = useState<boolean>(false);
  const [resendSMSConfirmation, setResendSMSConfirmation] = useState<boolean>(false);
  const [previousBookingStatusName, setPreviousBookingStatusName] = useState<BookingStatusNames>(bookingStatusName)

  useEffect(() => {
    if (bookingStatusName === BookingStatusNames.Cancelled || previousBookingStatusName === BookingStatusNames.Cancelled) {
      resetConfirmationCheckboxes()
    }
    setPreviousBookingStatusName(bookingStatusName)
  }, [bookingStatusName, previousBookingStatusName])

  const resetConfirmationCheckboxes = useCallback(() => {
    setResendConfirmation(false)
    handleResendConfirmationUpdate(false)
    setResendSMSConfirmation(false)
    handleResendSMSConfirmationUpdate(false)
  }, [])

  const useStyles = getMaterialStyles();
  const classes = useStyles();
  const showDeleteButton = isManagerAndLocalEnv;
  return (
    <div className={style.root}>
      {renderIf(errorMsg, () => (
        <div className={style.errorWrap}>
          <Alert severity="error" variant="filled" className={style.errorMsg}>
            {errorMsg}
          </Alert>
        </div>
      ))}
      {renderIf(paxThresholdError, () => (
        <Typography className={style.paxThresholdErrorMessage} variant={'h5'}>
          Pax change doesn't meet the requirement for an option added
        </Typography>
      ))}
      <div className={style.btnWrap}>
        <div>
          {renderIf(showDeleteButton, () => (
            <Button variant="text" size="medium"
              disabled={isLoading}
              className={classNames({
                [classes.btnLeft]: true,
                [classes.btnDefault]: true
              })} onClick={() => {
                setShowDeleteConfirmation(true);
              }}>
              Delete
            </Button>
          ))}

          {renderIf(showDateChange, () => (
            <Button variant="text" size="medium"
              disabled={isLockedBooking || isLoading}
              className={classNames({
                [classes.btnLeft]: true,
                [classes.btnDefault]: true
              })} onClick={handleDateChangeClick}>
              Change date
            </Button>
          ))}

          {renderIf(bookingStatusCode === "TX", () => (
            <Button variant="text" size="medium"
              disabled={isLoading}
              className={classNames({
                [classes.btnLeft]: true,
                [classes.btnDefault]: true
              })} onClick={handleSendSMSClick}>
              Resend SMS
            </Button>
          ))}
        </div>

        <div>

          {renderIf(!isStandbyOrWaitlist && customer?.phone && isSmsEnabled && (isStatusNameInList(bookingStatusName, [bookingStatusType.confirmed, bookingStatusType.unconfirmed])) , () => (
            <FormControlLabel
              disabled={disableNotification ||isLoading}
              className={classes.checkboxFC}
              control={
                <Checkbox
                  className={classes.checkbox}
                  color="default"
                  size="medium"
                  icon={<CheckBoxOutlineBlankIcon fontSize="large" />}
                  checkedIcon={<CheckBoxIcon fontSize="large" />}
                  name="checkedI"
                  checked={disableNotification ? false : resendSMSConfirmation}
                  onChange={(evt, checked) => {
                    setResendSMSConfirmation(checked);
                    handleResendSMSConfirmationUpdate(checked);
                  }}
                />
              }
                label= "Resend SMS confirmation"
            />
          ))}

          {renderIf(!isStandbyOrWaitlist && customer?.email && (
            isStatusNameInList(bookingStatusName, [bookingStatusType.confirmed, bookingStatusType.unconfirmed, bookingStatusType.cancelled, bookingStatusType.pendingPayment])
          ), () => (
            <FormControlLabel
              disabled={disableNotification || isLoading}
              className={classes.checkboxFC}
              control={
                <Checkbox
                  className={classes.checkbox}
                  color="default"
                  size="medium"
                  icon={<CheckBoxOutlineBlankIcon fontSize="large" />}
                  checkedIcon={<CheckBoxIcon fontSize="large" />}
                  name="checkedI"
                  checked={disableNotification ? false : resendConfirmation}
                  onChange={(evt, checked) => {
                    setResendConfirmation(checked);
                    handleResendConfirmationUpdate(checked);
                  }}
                />
              }
                label= {bookingStatusName === BookingStatusNames.Cancelled ? "Send email cancellation" : "Resend email confirmation" }
            />
          ))}
          <Button variant="text" size="medium"
            disabled={isLoading}
            className={classNames({
              [classes.btnRight]: true,
              [classes.btnDefault]: true
            })} onClick={handleCloseClick}>
            Close
          </Button>

          <SaveBtn variant="contained" color="primary" size="medium"
            disabled={isLockedBooking || !isSaveEnabled || isLoading || paxThresholdError}
            startIcon={<DoneIcon />}
            className={classes.btnRight}
            onClick={handleSaveClick}>
            Save
          </SaveBtn>
        </div>
      </div>


      <SimpleDialog
        isOpen={showDeleteConfirmation}
        title="Are you sure?"
        description="The booking will be cancelled. If the customer is a no show please change the booking status."
        confirmText="Confirm"
        rejectText="No"
        handleConfirm={() => {
          handleDeleteConfirmed();
          setShowDeleteConfirmation(false);
        }}
        handleReject={() => {
          handleDeleteCancelled();
          setShowDeleteConfirmation(false);
        }}
      />
    </div>
  )
}

